import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import { MODAL_SUBTITLE_OPTIONS_PREVIEW_LABEL } from "../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../lib/i18next";
import {
  DEFAULT_SUBTITLE_OPTIONS,
  SUBTITLE_PRESET_BOXED,
  resolveSubtitlePreset,
} from "./subtitlePresets";

const getFontStyleFromOption = (fontStyle) => {
  switch (fontStyle) {
    case "bold":
      return { fontWeight: 700, fontStyle: "normal" };
    case "italic":
      return { fontWeight: 400, fontStyle: "italic" };
    case "bold_italic":
      return { fontWeight: 700, fontStyle: "italic" };
    case "normal":
      return { fontWeight: 400, fontStyle: "normal" };
    default:
      return { fontWeight: 400, fontStyle: "normal" };
  }
};

const TEXT_STROKE =
  "1px 1px 0 #000, -1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 2px 0 0 #000, -2px 0 0 #000, 0 2px 0 #000, 0 -2px 0 #000";

const KARAOKE_CHAR_DELAY_MS = 150;
const WORD_DELAY_MS = 700;
const LOOP_PAUSE_MS = 1500;

const BoxedSubtitlePreview = ({
  words,
  currentWordIndex,
  subtitleColor,
  backgroundColor,
  fontFamily,
  fontWeight,
  cssFontStyle,
}) => {
  const containerRef = useRef(null);
  const wordRefs = useRef([]);
  const [bar, setBar] = useState({
    left: 0,
    width: 0,
    top: 0,
    visible: false,
  });

  const displayWordIndex =
    currentWordIndex === -1 ? words.length - 1 : currentWordIndex;

  useLayoutEffect(() => {
    const wordEl = wordRefs.current[displayWordIndex];
    const container = containerRef.current;
    if (!wordEl || !container) return;
    const c = container.getBoundingClientRect();
    const w = wordEl.getBoundingClientRect();
    setBar({
      left: w.left - c.left,
      width: w.width,
      top: w.bottom - c.top + 1,
      visible: true,
    });
  }, [
    displayWordIndex,
    words,
    fontFamily,
    fontWeight,
    cssFontStyle,
    backgroundColor,
  ]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        display: "inline-block",
        backgroundColor,
        px: 1.5,
        py: 1,
        maxWidth: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          columnGap: "0.35em",
          rowGap: "0.2em",
        }}
      >
        {words.map((word, i) => (
          <Box
            component="span"
            key={`${word}-${i}`}
            ref={(el) => {
              wordRefs.current[i] = el;
            }}
            sx={{
              color: subtitleColor,
              fontFamily: `"${fontFamily}", sans-serif`,
              fontWeight,
              fontStyle: cssFontStyle,
              fontSize: 18,
              lineHeight: 1.35,
            }}
          >
            {word}
          </Box>
        ))}
      </Box>
      {bar.visible && (
        <Box
          sx={{
            position: "absolute",
            left: bar.left,
            top: bar.top,
            width: bar.width,
            height: 3,
            backgroundColor: subtitleColor,
            transition: "left 160ms ease, width 160ms ease, top 160ms ease",
            pointerEvents: "none",
          }}
        />
      )}
    </Box>
  );
};

const SubtitlePreview = ({
  options = {},
  sampleText = MODAL_SUBTITLE_OPTIONS_PREVIEW_LABEL,
}) => {
  const opts = { ...DEFAULT_SUBTITLE_OPTIONS, ...options };
  const {
    fontFamily,
    fontStyle,
    subtitleColor,
    doHighlight,
    highlightColor,
    highlightMode,
    hasBackground,
    backgroundColor,
  } = opts;
  const preset = resolveSubtitlePreset(opts);
  const isBoxed = preset === SUBTITLE_PRESET_BOXED;
  const isWordTimed =
    doHighlight && (highlightMode === "word" || highlightMode === "underline");

  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);

  const { t } = useTranslationApp();
  const translatedSampleText = t(sampleText);
  const chars = translatedSampleText.split("");
  const words = translatedSampleText.split(/\s+/).filter(Boolean);

  useEffect(() => {
    if (!doHighlight || highlightMode !== "karaoke") return;

    const totalChars = chars.length;
    if (totalChars === 0) return;

    const interval = setInterval(() => {
      setCurrentCharIndex((prev) => {
        if (prev >= totalChars - 1) return -1;
        return prev + 1;
      });
    }, KARAOKE_CHAR_DELAY_MS);

    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [doHighlight, highlightMode, translatedSampleText]);

  useEffect(() => {
    if (!doHighlight || highlightMode !== "karaoke") return;
    if (currentCharIndex === -1) {
      const id = setTimeout(() => setCurrentCharIndex(0), LOOP_PAUSE_MS);
      return () => clearTimeout(id);
    }
  }, [currentCharIndex, doHighlight, highlightMode]);

  useEffect(() => {
    if (!isWordTimed) return;

    const totalWords = words.length;
    if (totalWords === 0) return;

    const interval = setInterval(() => {
      setCurrentWordIndex((prev) => {
        if (prev >= totalWords - 1) return -1;
        return prev + 1;
      });
    }, WORD_DELAY_MS);

    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [isWordTimed, translatedSampleText]);

  useEffect(() => {
    if (!isWordTimed) return;
    if (currentWordIndex === -1) {
      const id = setTimeout(() => setCurrentWordIndex(0), LOOP_PAUSE_MS);
      return () => clearTimeout(id);
    }
  }, [currentWordIndex, isWordTimed]);

  const { fontWeight, fontStyle: cssFontStyle } =
    getFontStyleFromOption(fontStyle);
  const baseTextStyle = {
    fontFamily: `"${fontFamily}", sans-serif`,
    fontSize: 18,
    fontWeight,
    fontStyle: cssFontStyle,
    textShadow: isBoxed ? "none" : TEXT_STROKE,
    display: "inline",
  };

  const renderContent = () => {
    if (isBoxed) {
      return (
        <BoxedSubtitlePreview
          words={words}
          currentWordIndex={currentWordIndex}
          subtitleColor={subtitleColor}
          backgroundColor={backgroundColor}
          fontFamily={fontFamily}
          fontWeight={fontWeight}
          cssFontStyle={cssFontStyle}
        />
      );
    }

    if (!doHighlight) {
      return (
        <Box
          component="span"
          sx={{
            ...baseTextStyle,
            color: subtitleColor,
          }}
        >
          {translatedSampleText}
        </Box>
      );
    }

    if (highlightMode === "karaoke") {
      const displayCharIndex =
        currentCharIndex === -1 ? chars.length - 1 : currentCharIndex;
      return (
        <Box component="span" sx={baseTextStyle}>
          {chars.map((char, i) => (
            <Box
              key={`${char}-${i}`}
              component="span"
              sx={{
                color: i <= displayCharIndex ? highlightColor : subtitleColor,
              }}
            >
              {char}
            </Box>
          ))}
        </Box>
      );
    }

    if (highlightMode === "word") {
      const displayWordIndex =
        currentWordIndex === -1 ? words.length - 1 : currentWordIndex;
      return (
        <Box component="span" sx={baseTextStyle}>
          {words.map((word, wordIdx) => {
            const isHighlighted = wordIdx === displayWordIndex;
            const isLast = wordIdx === words.length - 1;
            return (
              <Box key={`${word}-${wordIdx}`} component="span">
                <Box
                  component="span"
                  sx={{
                    color: isHighlighted ? highlightColor : subtitleColor,
                  }}
                >
                  {word}
                </Box>
                {!isLast && " "}
              </Box>
            );
          })}
        </Box>
      );
    }

    return (
      <Box component="span" sx={{ ...baseTextStyle, color: subtitleColor }}>
        {translatedSampleText}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        borderRadius: 1,
        p: 2,
        minHeight: 80,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          ...(!isBoxed &&
            hasBackground && {
              backgroundColor: `${backgroundColor}cc`,
              px: 2,
              py: 1,
              borderRadius: 1,
            }),
          maxWidth: "100%",
        }}
      >
        {renderContent()}
      </Box>
    </Box>
  );
};

export default SubtitlePreview;
